(function () {
  'use strict';

  /** @type {Window & Record<string, any>} */
  const runtimeWindow = window;
  const GLOBAL_KEY = 'AoneGoldPricing';
  const INIT_FLAG = '__aoneGoldPricingInitialized';
  const UPDATE_EVENT = 'aone:gold-prices-updated';

  const TROY_OUNCE_IN_GRAMS = 31.10;

  const PURITY_MULTIPLIERS = Object.freeze({
    '24k': 1,
    '22k': 0.9167,
    '18k': 0.75,
    '14k': 0.585,
    '10k': 0.417,
  });

  const MATERIAL_KEYS = Object.freeze(['10KT', '14KT', '18KT', '22KT', '24KT']);

  const PROP = Object.freeze({
    perTroyOz: '_gold_price_per_troy_oz',
    capturedAt: '_gold_price_captured_at',
    material: '_gold_price_material',
    currency: '_gold_price_currency',
    unit: '_gold_price_unit',
  });

  const CAPTURED_PRICE_KEYS = Object.freeze([
    '_gold_price_per_troy_oz',
    'gold_price_per_troy_oz',
    'goldPricePerTroyOz',
  ]);

  if (runtimeWindow[INIT_FLAG]) return;
  runtimeWindow[INIT_FLAG] = true;

  /**
   * spot24KPerTroyOz = Final Gold Price (Live + Premium) used for 10K/14K/… karat maps.
   * liveGoldPricePerTroyOz / goldPremium are retained for spot display and tooltips.
   * @type {{ spot24KPerTroyOz: number | null, liveGoldPricePerTroyOz: number | null, goldPremium: number, currency: string | null, status: 'idle' | 'loading' | 'ready' | 'error', lastUpdated: string | null, fetchInFlight: boolean, intervalId: number | null, config: { enabled: boolean, baseUrl: string, autoRefresh: boolean, intervalMs: number } | null, configSignature: string, readyWaiters: Array<{ resolve: (v: boolean) => void }>, abortController: AbortController | null, readyPromise: Promise<boolean> | null }}
   */
  const store = {
    spot24KPerTroyOz: null,
    liveGoldPricePerTroyOz: null,
    goldPremium: 0,
    currency: null,
    status: 'idle',
    lastUpdated: null,
    fetchInFlight: false,
    intervalId: null,
    config: null,
    configSignature: '',
    readyWaiters: [],
    abortController: null,
    readyPromise: null,
  };

  /** @param {unknown} value @param {boolean} [strictPositive] */
  function parseFinitePositiveNumber(value, strictPositive) {
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return null;
      if (strictPositive && value <= 0) return null;
      return value >= 0 ? value : null;
    }
    if (typeof value !== 'string') return null;

    const cleaned = value
      .replace(/,/g, '')
      .replace(/[^\d.-]/g, '')
      .trim();

    if (!cleaned) return null;

    const parsed = Number(cleaned);
    if (!Number.isFinite(parsed)) return null;
    if (strictPositive && parsed <= 0) return null;
    return parsed >= 0 ? parsed : null;
  }

  /** @param {unknown} value */
  function parseWeightInGrams(value) {
    if (typeof value === 'number') {
      return Number.isFinite(value) && value >= 0 ? value : null;
    }
    if (value == null) return null;

    const cleaned = String(value)
      .replace(',', '.')
      .replace(/[^\d.-]/g, '');

    if (!cleaned) return null;

    const parsed = Number(cleaned);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }

  /** @param {string | null | undefined} value */
  function normalizeJewelryMaterial(value) {
    if (value == null) return null;

    const raw = String(value).trim();
    if (!raw) return null;

    const match = raw.toUpperCase().match(/(?:^|[^0-9])(10|14|18|22|24)\s*(?:K|KT|KARAT)(?:[^A-Z]|$)/i);
    if (!match) return null;

    const karat = Number.parseInt(match[1], 10);
    if (!Number.isFinite(karat)) return null;

    const karatKey = `${karat}k`;
    if (!Object.prototype.hasOwnProperty.call(PURITY_MULTIPLIERS, karatKey)) return null;

    return `${karat}KT`;
  }

  /**
   * Normalize material strings to ticker karat keys like `14K`.
   * @param {unknown} value
   * @returns {'24K'|'22K'|'18K'|'14K'|'10K'|null}
   */
  function normalizeGoldKarat(value) {
    const normalized = String(value || '')
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '');

    const match = normalized.match(/(24|22|18|14|10)K(?:T)?/);
    if (!match) return null;

    const karatKey = `${match[1]}k`;
    if (!Object.prototype.hasOwnProperty.call(PURITY_MULTIPLIERS, karatKey)) return null;
    return /** @type {'24K'|'22K'|'18K'|'14K'|'10K'} */ (`${match[1]}K`);
  }

  /** @param {unknown} value @param {number | null} [fallback] */
  function toFiniteNumber(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback == null ? null : fallback;
  }

  /**
   * Round money/pricing values to exactly 2 decimal places BEFORE the next calculation.
   * Returns a number (never a string). Non-finite inputs become 0.
   * Uses exponential-string rounding to avoid binary float artifacts at .xx5 boundaries.
   * @param {unknown} value
   * @returns {number}
   */
  function round2(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return 0;
    return Number(Math.round(number + 'e2') + 'e-2');
  }

  /** @param {string | null | undefined} name */
  function isMaterialOptionName(name) {
    if (name == null) return false;
    const up = String(name).trim().toUpperCase();
    if (!up) return false;
    return (
      up === 'JEWELRY MATERIAL' ||
      up === 'MATERIAL' ||
      up === 'GOLD KARAT' ||
      up === 'KARAT' ||
      up === 'PURITY' ||
      up.includes('MATERIAL') ||
      up.includes('KARAT') ||
      up.includes('PURITY')
    );
  }

  /** @param {string | null | undefined} material */
  function materialToKaratKey(material) {
    const normalized = normalizeJewelryMaterial(material);
    if (!normalized) return null;
    const karat = normalized.replace(/KT$/i, '');
    const key = `${karat}k`.toLowerCase();
    return Object.prototype.hasOwnProperty.call(PURITY_MULTIPLIERS, key) ? key : null;
  }

  function getActiveCurrencyCode() {
    const shopCurrency = runtimeWindow.Shopify && runtimeWindow.Shopify.currency && runtimeWindow.Shopify.currency.active;
    if (typeof shopCurrency === 'string' && shopCurrency.trim()) return shopCurrency.trim();
    if (store.currency) return store.currency;
    return 'USD';
  }

  /** @param {number | null} spot24KPerTroyOz */
  function buildLiveGoldPriceMap(spot24KPerTroyOz) {
    /** @type {Record<string, { perTroyOz: number, currency: string, unit: string, material: string }>} */
    const map = {};
    if (spot24KPerTroyOz == null || !Number.isFinite(spot24KPerTroyOz) || spot24KPerTroyOz <= 0) {
      return map;
    }

    const currency = store.currency || getActiveCurrencyCode();
    const roundedSpot = round2(spot24KPerTroyOz);
    MATERIAL_KEYS.forEach(function (materialKey) {
      const karatKey = materialToKaratKey(materialKey);
      if (!karatKey) return;
      map[materialKey] = {
        material: materialKey,
        // Purity result is rounded before any per-gram conversion.
        perTroyOz: round2(roundedSpot * PURITY_MULTIPLIERS[karatKey]),
        currency,
        unit: 'per_troy_oz',
      };
    });
    return map;
  }

  function getLiveGoldPriceMap() {
    return buildLiveGoldPriceMap(store.spot24KPerTroyOz);
  }

  /** @param {Record<string, unknown> | null | undefined} record */
  function extractPerTroyOzPrice(record) {
    if (!record || typeof record !== 'object') return null;
    const candidates = [
      record.per_troy_oz,
      record.price_per_troy_oz,
      record.pricePerTroyOz,
      record.perTroyOz,
      record.price,
      record.ask,
      record.current_price,
      record.liveGoldPrice,
      record.goldPrice,
      record.gold_price,
    ];
    for (let i = 0; i < candidates.length; i += 1) {
      const parsed = parseFinitePositiveNumber(candidates[i], true);
      if (parsed != null) return parsed;
    }
    return null;
  }

  /** @param {Record<string, unknown> | null | undefined} properties */
  function readCapturedPriceRaw(properties) {
    if (!properties || typeof properties !== 'object') return null;
    for (let i = 0; i < CAPTURED_PRICE_KEYS.length; i += 1) {
      const key = CAPTURED_PRICE_KEYS[i];
      if (properties[key] != null && String(properties[key]).trim() !== '') {
        return properties[key];
      }
    }
    return null;
  }

  /**
   * @param {{ lineProperties?: Record<string, unknown> | null, material?: string | null, liveGoldPriceMap?: Record<string, { perTroyOz?: number }> | null, allowLiveFallback?: boolean }} input
   */
  function resolveGoldPricePerTroyOz(input) {
    const props = input.lineProperties && typeof input.lineProperties === 'object' ? input.lineProperties : {};
    const normalizedMaterial = normalizeJewelryMaterial(input.material);
    const capturedRaw = readCapturedPriceRaw(props);
    const capturedPrice = parseFinitePositiveNumber(capturedRaw, true);
    const capturedMaterial = normalizeJewelryMaterial(props[PROP.material]);
    const capturedAt = props[PROP.capturedAt] != null ? String(props[PROP.capturedAt]).trim() : '';

    if (capturedPrice != null && capturedPrice > 0) {
      if (normalizedMaterial && capturedMaterial && capturedMaterial !== normalizedMaterial) {
        return {
          value: null,
          source: 'invalid',
          reason: 'material_mismatch',
          material: capturedMaterial,
          capturedAt,
        };
      }
      return {
        value: capturedPrice,
        source: 'captured',
        material: capturedMaterial || normalizedMaterial,
        capturedAt,
      };
    }

    const allowLiveFallback = input.allowLiveFallback !== false;
    if (allowLiveFallback && normalizedMaterial) {
      const liveMap = input.liveGoldPriceMap || getLiveGoldPriceMap();
      const liveRecord = liveMap[normalizedMaterial];
      const livePrice = extractPerTroyOzPrice(liveRecord) || liveRecord?.perTroyOz || null;
      if (livePrice != null && livePrice > 0) {
        return {
          value: livePrice,
          source: 'live-fallback',
          material: normalizedMaterial,
          capturedAt: '',
        };
      }
    }

    if (store.status === 'loading' || store.status === 'idle' || store.fetchInFlight) {
      return {
        value: null,
        source: 'loading',
        material: normalizedMaterial,
        capturedAt: '',
      };
    }

    return {
      value: null,
      source: 'unavailable',
      material: normalizedMaterial,
      capturedAt: '',
    };
  }

  /**
   * Gold Premium ($/troy oz) from /api/gold-price/latest.
   * Missing/null/empty → 0 (never NaN).
   * @param {any} payload
   */
  function extractGoldPremium(payload) {
    const candidates = [
      payload?.data?.goldPremium,
      payload?.data?.gold_premium,
      payload?.goldPremium,
      payload?.gold_premium,
    ];
    for (let i = 0; i < candidates.length; i += 1) {
      const parsed = parseFinitePositiveNumber(candidates[i], false);
      if (parsed != null) return parsed;
    }
    return 0;
  }

  /**
   * Final Gold Price = round2(round2(Live) + round2(Premium)).
   * Each input is rounded before addition; the sum is rounded before use downstream.
   * @param {number} liveGoldPricePerTroyOz
   * @param {unknown} [goldPremium]
   */
  function calculateFinalGoldPrice(liveGoldPricePerTroyOz, goldPremium) {
    const live = Number(liveGoldPricePerTroyOz);
    if (!Number.isFinite(live) || live <= 0) return null;
    const premiumParsed = parseFinitePositiveNumber(goldPremium, false);
    const premium = premiumParsed != null ? premiumParsed : 0;
    return round2(round2(live) + round2(premium));
  }

  /**
   * Variant tariff uplift on Gold $/g only.
   * Final = round2(Base × (1 + Tariff%/100)). Null/empty/0 tariff → round2(base).
   * Does not touch labour.
   * @param {number} baseGoldPricePerGram
   * @param {unknown} tariffPercentage
   */
  function applyVariantTariffToGoldPerGram(baseGoldPricePerGram, tariffPercentage) {
    const base = round2(baseGoldPricePerGram);
    if (!Number.isFinite(Number(baseGoldPricePerGram)) || Number(baseGoldPricePerGram) < 0) return 0;
    const pct = parseFinitePositiveNumber(tariffPercentage, false);
    if (pct == null || pct === 0) return base;
    return round2(base * (1 + round2(pct) / 100));
  }

  /**
   * @param {number} liveGoldPricePerTroyOz
   * @param {string | null} [currency]
   * @param {string | null} [updatedAt]
   * @param {unknown} [goldPremium]
   */
  function publishSpotPrice(liveGoldPricePerTroyOz, currency, updatedAt, goldPremium) {
    const live = Number(liveGoldPricePerTroyOz);
    if (!Number.isFinite(live) || live <= 0) return;

    const premiumParsed = parseFinitePositiveNumber(goldPremium, false);
    const premium = premiumParsed != null ? premiumParsed : 0;
    const finalGold = calculateFinalGoldPrice(live, premium);
    if (finalGold == null) return;

    store.liveGoldPricePerTroyOz = live;
    store.goldPremium = premium;
    // Karat maps / shared consumers use Final Gold (Live + Premium).
    store.spot24KPerTroyOz = finalGold;
    store.currency = currency || getActiveCurrencyCode();
    store.lastUpdated = updatedAt || new Date().toISOString();
    store.status = 'ready';
    clearTickerOwnerWatchdog();

    while (store.readyWaiters.length) {
      const waiter = store.readyWaiters.shift();
      if (waiter) waiter.resolve(true);
    }

    runtimeWindow.dispatchEvent(
      new CustomEvent(UPDATE_EVENT, {
        detail: {
          spot24KPerTroyOz: finalGold,
          liveGoldPricePerTroyOz: live,
          goldPremium: premium,
          finalGoldPricePerTroyOz: finalGold,
          currency: store.currency,
          lastUpdated: store.lastUpdated,
          liveGoldPriceMap: getLiveGoldPriceMap(),
          ratesPerGram: getRatesPerGram(),
        },
      })
    );
  }

  /** @param {string | null | undefined} material */
  function getGoldPriceForMaterial(material) {
    const normalizedMaterial = normalizeJewelryMaterial(material);
    if (!normalizedMaterial) return null;
    const liveMap = getLiveGoldPriceMap();
    const record = liveMap[normalizedMaterial];
    if (!record) return null;
    return {
      material: normalizedMaterial,
      karatKey: materialToKaratKey(normalizedMaterial),
      perTroyOz: record.perTroyOz,
      currency: record.currency,
      unit: record.unit,
    };
  }

  /**
   * @param {Record<string, unknown> | null | undefined} properties
   * @param {{ allowFallback?: boolean, rowMaterial?: string | null, cartCurrency?: string | null }} [opts]
   */
  function parseCapturedGoldPrice(properties, opts) {
    const props = properties && typeof properties === 'object' ? properties : {};
    const rowMaterial = opts && opts.rowMaterial ? normalizeJewelryMaterial(opts.rowMaterial) : null;
    const cartCurrency = opts && opts.cartCurrency ? String(opts.cartCurrency) : getActiveCurrencyCode();
    const resolved = resolveGoldPricePerTroyOz({
      lineProperties: props,
      material: rowMaterial,
      allowLiveFallback: opts ? opts.allowFallback !== false : true,
    });

    const storedCurrency = props[PROP.currency] != null ? String(props[PROP.currency]).trim() : '';
    const unit = props[PROP.unit] != null ? String(props[PROP.unit]).trim() : 'per_troy_oz';

    if (resolved.source === 'captured' && resolved.value != null) {
      if (storedCurrency && storedCurrency !== cartCurrency) {
        return {
          status: 'invalid',
          reason: 'currency_mismatch',
          perTroyOz: null,
          capturedAt: resolved.capturedAt || '',
          material: resolved.material || rowMaterial,
          currency: storedCurrency,
          unit,
          isFallback: false,
        };
      }
      return {
        status: 'captured',
        perTroyOz: resolved.value,
        capturedAt: resolved.capturedAt || '',
        material: resolved.material || rowMaterial,
        currency: storedCurrency || cartCurrency,
        unit,
        isFallback: false,
      };
    }

    if (resolved.source === 'live-fallback' && resolved.value != null) {
      return {
        status: 'fallback',
        perTroyOz: resolved.value,
        capturedAt: '',
        material: resolved.material || rowMaterial,
        currency: cartCurrency,
        unit: 'per_troy_oz',
        isFallback: true,
      };
    }

    if (resolved.source === 'loading') {
      return {
        status: 'loading',
        perTroyOz: null,
        capturedAt: '',
        material: resolved.material || rowMaterial,
        currency: cartCurrency,
        unit,
        isFallback: false,
      };
    }

    if (resolved.source === 'invalid') {
      return {
        status: 'invalid',
        reason: resolved.reason || 'invalid',
        perTroyOz: null,
        capturedAt: resolved.capturedAt || '',
        material: resolved.material || rowMaterial,
        currency: cartCurrency,
        unit,
        isFallback: false,
      };
    }

    return {
      status: 'unavailable',
      perTroyOz: null,
      capturedAt: '',
      material: rowMaterial,
      currency: cartCurrency,
      unit,
      isFallback: false,
    };
  }

  /** @param {string | null | undefined} material */
  function buildCaptureProperties(material) {
    const live = getGoldPriceForMaterial(material);
    if (!live) return null;

    return {
      [PROP.perTroyOz]: String(Number(live.perTroyOz.toFixed(4))),
      [PROP.capturedAt]: new Date().toISOString(),
      [PROP.material]: live.material,
      [PROP.currency]: live.currency,
      [PROP.unit]: live.unit,
    };
  }

  /**
   * @param {Record<string, string>} properties
   * @param {string | null | undefined} material
   */
  function attachCaptureProperties(properties, material) {
    const next = properties && typeof properties === 'object' ? { ...properties } : {};
    const capture = buildCaptureProperties(material);
    if (!capture) return next;
    return Object.assign(next, capture);
  }

  /** @param {number} perTroyOz */
  function calculateGoldPricePerGram(perTroyOz) {
    if (!Number.isFinite(perTroyOz) || perTroyOz <= 0) return 0;
    return round2(perTroyOz / TROY_OUNCE_IN_GRAMS);
  }

  /**
   * @param {number} perTroyOz
   * @param {number} totalWeightGrams
   */
  function calculateRowGoldValue(perTroyOz, totalWeightGrams) {
    if (!Number.isFinite(perTroyOz) || perTroyOz <= 0) return 0;
    if (!Number.isFinite(totalWeightGrams) || totalWeightGrams < 0) return 0;
    const goldPerGram = calculateGoldPricePerGram(perTroyOz);
    const weight = round2(totalWeightGrams);
    return round2(goldPerGram * weight);
  }

  /**
   * @param {number} goldValueDollars
   * @param {number | null} labourCents
   */
  function calculateRowTotalCents(goldValueDollars, labourCents) {
    const labour = labourCents == null || !Number.isFinite(labourCents) ? 0 : labourCents;
    const gold = Number.isFinite(goldValueDollars) ? round2(goldValueDollars) : 0;
    return Math.round(round2(gold + round2(labour / 100)) * 100);
  }

  /**
   * Cart row pricing with strict 2-decimal chaining:
   * unitPrice = round2(round2(gold) + round2(labour))
   * totalCost = round2(unitPrice × round2(weight))
   * @param {{ totalWeight: unknown, liveGoldPricePerGram: unknown, labourPerGram: unknown }} input
   */
  function calculateCartRowPricing(input) {
    const safeWeight = toFiniteNumber(input.totalWeight);
    const safeGoldPrice = toFiniteNumber(input.liveGoldPricePerGram);
    const safeLabour = toFiniteNumber(input.labourPerGram);

    if (safeWeight === null || safeGoldPrice === null || safeLabour === null) {
      return null;
    }

    const goldPricePerGram = round2(safeGoldPrice);
    const labourPerGram = round2(safeLabour);
    const totalWeight = round2(safeWeight);
    const unitPricePerGram = round2(goldPricePerGram + labourPerGram);
    const totalCost = round2(unitPricePerGram * totalWeight);

    return {
      goldPricePerGram,
      labourPerGram,
      totalWeight,
      unitPricePerGram,
      totalCost,
    };
  }

  /** @param {string | null | undefined} material @param {string | null} [cartCurrency] */
  function resolveLiveGoldPrice(material, cartCurrency) {
    const normalizedMaterial = normalizeJewelryMaterial(material);
    const currency = cartCurrency || getActiveCurrencyCode();
    const karat = normalizeGoldKarat(normalizedMaterial || material);

    if (!normalizedMaterial || !karat) {
      return {
        status: 'unavailable',
        reason: 'material_unresolved',
        perTroyOz: null,
        goldPricePerGram: null,
        material: normalizedMaterial,
        karat,
        currency,
        unit: 'per_gram',
        updatedAt: store.lastUpdated,
        isFallback: false,
        capturedAt: '',
      };
    }

    const live = getGoldPriceForMaterial(normalizedMaterial);
    if (live && Number.isFinite(live.perTroyOz) && live.perTroyOz > 0) {
      return {
        status: 'live',
        perTroyOz: live.perTroyOz,
        goldPricePerGram: calculateGoldPricePerGram(live.perTroyOz),
        material: normalizedMaterial,
        karat,
        currency: live.currency || currency,
        unit: 'per_gram',
        updatedAt: store.lastUpdated,
        isFallback: false,
        capturedAt: store.lastUpdated || '',
      };
    }

    if (store.status === 'loading' || store.status === 'idle' || store.fetchInFlight) {
      return {
        status: 'loading',
        perTroyOz: null,
        goldPricePerGram: null,
        material: normalizedMaterial,
        karat,
        currency,
        unit: 'per_gram',
        updatedAt: store.lastUpdated,
        isFallback: false,
        capturedAt: '',
      };
    }

    return {
      status: 'unavailable',
      reason: 'live_rate_missing',
      perTroyOz: null,
      goldPricePerGram: null,
      material: normalizedMaterial,
      karat,
      currency,
      unit: 'per_gram',
      updatedAt: store.lastUpdated,
      isFallback: false,
      capturedAt: '',
    };
  }

  /** @returns {Record<'24K'|'22K'|'18K'|'14K'|'10K', number | null>} */
  function getRatesPerGram() {
    /** @type {Record<'24K'|'22K'|'18K'|'14K'|'10K', number | null>} */
    const rates = {
      '24K': null,
      '22K': null,
      '18K': null,
      '14K': null,
      '10K': null,
    };
    const liveMap = getLiveGoldPriceMap();
    Object.keys(rates).forEach(function (karat) {
      const material = `${String(karat).replace(/K$/i, '')}KT`;
      const record = liveMap[material];
      if (record && Number.isFinite(record.perTroyOz) && record.perTroyOz > 0) {
        rates[/** @type {'24K'|'22K'|'18K'|'14K'|'10K'} */ (karat)] = calculateGoldPricePerGram(record.perTroyOz);
      }
    });
    return rates;
  }

  /** @param {number} cents @param {string} code @param {boolean} [withCode] */
  function formatMoney(cents, code, withCode) {
    if (!Number.isFinite(cents)) return '';
    const amount = cents / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code || 'USD',
        currencyDisplay: withCode ? 'code' : 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      return `${code || ''} ${amount.toFixed(2)}`.trim();
    }
  }

  /** @param {number} value @param {string} code */
  function formatPerTroyOz(value, code) {
    if (!Number.isFinite(value)) return '';
    return `${formatMoney(Math.round(value * 100), code, false)}/oz`;
  }

  /** @param {number} value @param {string} code */
  function formatPerGram(value, code) {
    if (!Number.isFinite(value)) return '';
    return `${formatMoney(Math.round(value * 100), code, false)}/g`;
  }

  /** @param {string | null | undefined} isoString */
  function formatCapturedTimestamp(isoString) {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '';
    const formatted = new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
    return `Added on ${formatted}`;
  }

  /** @param {string | null | undefined} isoString */
  function formatLiveTickerTimestamp(isoString) {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '';

    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const time = new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);

    if (isToday) return `Today, ${time}`;

    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  }

  /**
   * Cart pricing uses live Gold Ticker rates + Labour Per GM.
   * Formula (strict 2-decimal chaining at every step):
   *   Total Cost = round2(round2(Unit $/g) × round2(Weight))
   *   Unit $/g   = round2(round2(SKU Gold $/g) + round2(Labour $/g))
   *   SKU Gold $/g = round2(base karat Gold $/g × (1 + tariff%/100))
   * Labour is never tariff-adjusted here (Shopify labour stays base / untariffed).
   * @param {{ rowMaterial?: string | null, properties?: Record<string, unknown>, totalWeightGrams?: unknown, labourCents?: number | null, labourResolved?: boolean, cartCurrency?: string, allowFallback?: boolean, preferLive?: boolean, tariffPercentage?: unknown }} input
   */
  function calculateRowPricing(input) {
    const totalWeightGramsRaw = parseWeightInGrams(input.totalWeightGrams);
    const labourResolved = input.labourResolved === true;
    const labourCents =
      labourResolved && input.labourCents != null && Number.isFinite(input.labourCents)
        ? input.labourCents
        : null;
    const cartCurrency = input.cartCurrency || getActiveCurrencyCode();
    const preferLive = input.preferLive !== false;
    const tariffPercentage = input.tariffPercentage;

    /** @type {Record<string, unknown>} */
    let gold;

    if (preferLive) {
      gold = resolveLiveGoldPrice(input.rowMaterial || null, cartCurrency);
    } else {
      gold = parseCapturedGoldPrice(input.properties || {}, {
        allowFallback: input.allowFallback !== false,
        rowMaterial: input.rowMaterial || null,
        cartCurrency,
      });
      if (gold.status === 'captured' || gold.status === 'fallback') {
        gold = Object.assign({}, gold, {
          goldPricePerGram:
            gold.perTroyOz != null ? calculateGoldPricePerGram(Number(gold.perTroyOz)) : null,
          karat: normalizeGoldKarat(/** @type {string | null} */ (gold.material) || input.rowMaterial),
          updatedAt: gold.capturedAt || store.lastUpdated,
        });
      }
    }

    const emptyResult = function (status) {
      return {
        status,
        gold,
        goldValueCents: null,
        rowTotalCents: null,
        goldPricePerGram: null,
        baseGoldPricePerGram: null,
        labourPerGram: labourCents != null ? round2(labourCents / 100) : null,
        unitPricePerGram: null,
        totalWeightGrams: totalWeightGramsRaw != null ? round2(totalWeightGramsRaw) : totalWeightGramsRaw,
        goldRateUpdatedAt: gold && gold.updatedAt != null ? gold.updatedAt : store.lastUpdated,
      };
    };

    if (gold.status === 'loading') {
      return emptyResult('loading');
    }

    if (
      gold.status === 'invalid' ||
      gold.status === 'unavailable' ||
      (gold.perTroyOz == null && gold.goldPricePerGram == null)
    ) {
      return emptyResult(/** @type {string} */ (gold.status) || 'unavailable');
    }

    if (totalWeightGramsRaw == null || totalWeightGramsRaw <= 0) {
      return emptyResult('unavailable');
    }

    if (labourCents == null) {
      return emptyResult('unavailable');
    }

    const totalWeightGrams = round2(totalWeightGramsRaw);

    const baseGoldPricePerGram = round2(
      gold.goldPricePerGram != null && Number.isFinite(Number(gold.goldPricePerGram))
        ? Number(gold.goldPricePerGram)
        : calculateGoldPricePerGram(Number(gold.perTroyOz))
    );
    // Tariff applies once, only to the gold component (never to labour). Already round2 inside.
    const goldPricePerGram = applyVariantTariffToGoldPerGram(baseGoldPricePerGram, tariffPercentage);
    const labourPerGram = round2(labourCents / 100);
    const priced = calculateCartRowPricing({
      totalWeight: totalWeightGrams,
      liveGoldPricePerGram: goldPricePerGram,
      labourPerGram,
    });

    if (!priced) {
      return emptyResult('unavailable');
    }

    const goldValueDollars = round2(goldPricePerGram * totalWeightGrams);
    const goldValueCents = Math.round(goldValueDollars * 100);
    const rowTotalCents = Math.round(priced.totalCost * 100);

    return {
      status: 'ok',
      gold,
      goldValueCents,
      rowTotalCents,
      goldPricePerGram,
      baseGoldPricePerGram,
      labourPerGram,
      unitPricePerGram: priced.unitPricePerGram,
      totalWeightGrams,
      goldRateUpdatedAt: gold.updatedAt != null ? gold.updatedAt : store.lastUpdated,
    };
  }

  /** @param {any} payload */
  function extractSpotPrice(payload) {
    const latest = payload?.data?.latest ?? payload?.latest ?? null;
    const historyFirst =
      payload?.data?.last20SuccessfulUpdates?.[0] ?? payload?.last20SuccessfulUpdates?.[0] ?? null;

    const candidates = [
      latest?.liveGoldPrice,
      latest?.goldPM,
      latest?.goldAM,
      latest?.per_troy_oz,
      latest?.price_per_troy_oz,
      latest?.pricePerTroyOz,
      historyFirst?.liveGoldPrice,
      historyFirst?.goldPM,
      historyFirst?.goldAM,
      historyFirst?.per_troy_oz,
      payload?.data?.latest?.liveGoldPrice,
      payload?.data?.latest?.goldPM,
      payload?.data?.latest?.goldAM,
      payload?.data?.last20SuccessfulUpdates?.[0]?.liveGoldPrice,
      payload?.goldPrice,
      payload?.gold_price,
      payload?.price,
      payload?.latestPrice,
      payload?.latest_price,
      payload?.data?.price,
      payload?.data?.goldPrice,
      payload?.data?.gold_price,
    ];

    for (const candidate of candidates) {
      const numberValue = parseFinitePositiveNumber(candidate, true);
      if (numberValue != null) return numberValue;
    }
    return null;
  }

  /** @param {HTMLElement} container */
  function readFetchConfig(container) {
    const enabled = container.dataset.goldPriceEnabled === 'true';
    const autoRefresh = container.dataset.goldPriceAutoRefresh === 'true';
    const baseUrl = (container.dataset.goldPriceApiBaseUrl || '').trim();
    const rawInterval = Number.parseInt(container.dataset.goldPriceRefreshInterval || '', 10);
    const intervalSeconds = Number.isFinite(rawInterval) && rawInterval > 0 ? rawInterval : 60;
    return {
      enabled: enabled && baseUrl.length > 0,
      baseUrl,
      autoRefresh,
      intervalMs: intervalSeconds * 1000,
    };
  }

  /** @param {string} baseUrl */
  function buildEndpoint(baseUrl) {
    return `${baseUrl.replace(/\/+$/, '')}/api/gold-price/latest`;
  }

  /** @param {string} url */
  function buildRequestHeaders(url) {
    /** @type {Record<string, string>} */
    const headers = { accept: 'application/json' };
    try {
      const host = new URL(url, window.location.origin).hostname.toLowerCase();
      if (host.includes('ngrok')) {
        headers['ngrok-skip-browser-warning'] = 'true';
      }
    } catch {
      /* ignore invalid URL */
    }
    return headers;
  }

  function clearRefreshInterval() {
    if (store.intervalId == null) return;
    window.clearInterval(store.intervalId);
    store.intervalId = null;
  }

  function waitForReady() {
    if (store.status === 'ready' && store.spot24KPerTroyOz != null) {
      return Promise.resolve(true);
    }

    if (store.readyPromise) return store.readyPromise;

    store.readyPromise = new Promise(function (resolve) {
      const finish = function (result) {
        store.readyPromise = null;
        resolve(result);
      };

      if (store.status === 'ready' && store.spot24KPerTroyOz != null) {
        finish(true);
        return;
      }

      store.readyWaiters.push({
        resolve: function (result) {
          finish(result);
        },
      });

      const onUpdated = function () {
        if (store.status === 'ready' && store.spot24KPerTroyOz != null) {
          runtimeWindow.removeEventListener(UPDATE_EVENT, onUpdated);
          finish(true);
        }
      };
      runtimeWindow.addEventListener(UPDATE_EVENT, onUpdated);

      window.setTimeout(function () {
        runtimeWindow.removeEventListener(UPDATE_EVENT, onUpdated);
        finish(store.status === 'ready' && store.spot24KPerTroyOz != null);
      }, 12000);
    });

    return store.readyPromise;
  }

  async function refresh() {
    if (!store.config || !store.config.enabled || store.fetchInFlight) return;
    store.fetchInFlight = true;
    store.status = store.status === 'ready' ? 'ready' : 'loading';
    store.abortController?.abort();
    store.abortController = new AbortController();

    try {
      const endpoint = buildEndpoint(store.config.baseUrl);
      const response = await fetch(endpoint, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: buildRequestHeaders(endpoint),
        signal: store.abortController.signal,
      });
      if (!response.ok) {
        throw new Error(`Gold price request failed with status ${response.status}`);
      }
      const payload = await response.json();
      const spotPrice = extractSpotPrice(payload);
      if (spotPrice == null) {
        throw new Error('Gold price response missing a numeric spot price');
      }
      publishSpotPrice(
        spotPrice,
        getActiveCurrencyCode(),
        new Date().toISOString(),
        extractGoldPremium(payload)
      );
    } catch (error) {
      if (error && /** @type {{ name?: string }} */ (error).name === 'AbortError') return;
      store.status = store.spot24KPerTroyOz != null ? 'ready' : 'error';
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[aone-gold-pricing] Failed to refresh live gold price', error);
      }
      while (store.readyWaiters.length) {
        const waiter = store.readyWaiters.shift();
        if (waiter) waiter.resolve(store.spot24KPerTroyOz != null);
      }
    } finally {
      store.fetchInFlight = false;
    }
  }

  /** @type {number | null} */
  let tickerOwnerWatchdogId = null;

  function clearTickerOwnerWatchdog() {
    if (tickerOwnerWatchdogId == null) return;
    window.clearTimeout(tickerOwnerWatchdogId);
    tickerOwnerWatchdogId = null;
  }

  /**
   * When the header gold ticker is live, it already polls /api/gold-price/latest
   * and pushes results into this module via updateFromApiPayload(). Starting a
   * second interval here doubles every network hit (e.g. every 30s).
   * @returns {boolean}
   */
  function isTickerOwnedFetch() {
    const ticker = document.querySelector('[data-gold-ticker-container]');
    if (!(ticker instanceof HTMLElement)) return false;
    const tickerEnabled = ticker.dataset.goldTickerEnabled === 'true';
    const liveEnabled = ticker.dataset.goldPriceEnabled === 'true';
    const baseUrl = (ticker.dataset.goldPriceApiBaseUrl || '').trim();
    return tickerEnabled && liveEnabled && baseUrl.length > 0;
  }

  /**
   * Start this module's own poll (used when no ticker owns the fetch, or as
   * fallback if the ticker never publishes a price).
   * @param {{ enabled: boolean, baseUrl: string, autoRefresh: boolean, intervalMs: number }} config
   */
  function startSelfOwnedFetch(config) {
    clearRefreshInterval();
    if (!config.enabled) return;
    refresh();
    if (config.autoRefresh) {
      store.intervalId = window.setInterval(refresh, config.intervalMs);
    }
  }

  /** @param {{ enabled: boolean, baseUrl: string, autoRefresh: boolean, intervalMs: number }} config */
  function mountFetch(config) {
    const tickerOwned = isTickerOwnedFetch();
    const signature = [
      config.enabled,
      config.baseUrl,
      config.autoRefresh,
      config.intervalMs,
      tickerOwned ? 'ticker' : 'self',
    ].join('|');
    const sameConfig = store.config && store.configSignature === signature;

    if (sameConfig && (store.status === 'ready' || store.fetchInFlight || tickerOwned)) {
      return;
    }

    store.config = config;
    store.configSignature = signature;
    clearRefreshInterval();
    clearTickerOwnerWatchdog();
    if (!config.enabled) return;

    // Ticker owns the network poll; cart/PLP still receive updates via UPDATE_EVENT.
    if (tickerOwned) {
      // If ticker.js fails to load or never publishes, take over after a short wait.
      tickerOwnerWatchdogId = window.setTimeout(function () {
        tickerOwnerWatchdogId = null;
        if (store.status === 'ready' && store.spot24KPerTroyOz != null) return;
        if (!isTickerOwnedFetch() || !store.config) return;
        store.configSignature = [
          store.config.enabled,
          store.config.baseUrl,
          store.config.autoRefresh,
          store.config.intervalMs,
          'self-fallback',
        ].join('|');
        startSelfOwnedFetch(store.config);
      }, 8000);
      return;
    }

    startSelfOwnedFetch(config);
  }

  /** @param {Document | HTMLElement | unknown} scope */
  function initFromDom(scope) {
    const root = scope instanceof HTMLElement || scope instanceof Document ? scope : document;
    const container =
      root.querySelector('[data-gold-ticker-container]') || root.querySelector('[data-gold-price-container]');
    if (!(container instanceof HTMLElement)) return;
    mountFetch(readFetchConfig(container));
  }

  /**
   * @param {any} payload
   * @param {{ currency?: string | null }} [meta]
   */
  function updateFromApiPayload(payload, meta) {
    const spotPrice = extractSpotPrice(payload);
    if (spotPrice == null) return false;
    publishSpotPrice(
      spotPrice,
      (meta && meta.currency) || getActiveCurrencyCode(),
      new Date().toISOString(),
      extractGoldPremium(payload)
    );
    return true;
  }

  /**
   * @param {{ row?: HTMLElement | null, variant?: { option1?: string | null } | null, optionValue?: string | null, productTitle?: string | null }} source
   */
  function resolveJewelryMaterial(source) {
    if (!source) return null;
    if (source.row instanceof HTMLElement) {
      if (source.row.dataset.goldPriceMaterial) {
        const fromCaptured = normalizeJewelryMaterial(source.row.dataset.goldPriceMaterial);
        if (fromCaptured) return fromCaptured;
      }
      const optionCell = source.row.querySelector(
        '.order-sheet-details-variant-table__option, .quick-view-variant-table__option, [data-jewelry-material-value]'
      );
      if (optionCell) {
        const fromCell = normalizeJewelryMaterial(optionCell.textContent || '');
        if (fromCell) return fromCell;
      }
      if (source.row.dataset.jewelryMaterial) {
        const fromData = normalizeJewelryMaterial(source.row.dataset.jewelryMaterial);
        if (fromData) return fromData;
      }
    }
    if (source.optionValue) {
      const fromOption = normalizeJewelryMaterial(source.optionValue);
      if (fromOption) return fromOption;
    }
    if (source.variant && source.variant.option1) {
      const fromVariant = normalizeJewelryMaterial(source.variant.option1);
      if (fromVariant) return fromVariant;
    }
    if (source.productTitle) {
      const fromTitle = normalizeJewelryMaterial(source.productTitle);
      if (fromTitle) return fromTitle;
    }
    return null;
  }

  /**
   * Resolve jewelry material for cart rows without using category attribute columns (e.g. WIDTH).
   * @param {{ properties?: Record<string, unknown>, options_with_values?: Array<{ name?: string, value?: string }>, optionsWithValues?: Array<{ name?: string, value?: string }>, row?: HTMLElement | null, productTitle?: string | null, product_title?: string | null, title?: string | null }} source
   */
  function resolveCartRowMaterial(source) {
    if (!source || typeof source !== 'object') return null;

    const props = source.properties && typeof source.properties === 'object' ? source.properties : {};
    const capturedKeys = [PROP.material, '_gold_price_material', 'gold_price_material'];
    for (let i = 0; i < capturedKeys.length; i += 1) {
      const key = capturedKeys[i];
      if (props[key] != null && String(props[key]).trim()) {
        const normalized = normalizeJewelryMaterial(String(props[key]).trim());
        if (normalized) return normalized;
      }
    }

    const options = source.options_with_values || source.optionsWithValues;
    if (Array.isArray(options)) {
      for (let j = 0; j < options.length; j += 1) {
        const opt = options[j];
        if (!opt || !isMaterialOptionName(opt.name)) continue;
        const normalized = normalizeJewelryMaterial(opt.value != null ? String(opt.value) : '');
        if (normalized) return normalized;
      }
    }

    const row = source.row;
    if (row instanceof HTMLElement) {
      if (row.dataset.goldPriceMaterial) {
        const fromCaptured = normalizeJewelryMaterial(row.dataset.goldPriceMaterial);
        if (fromCaptured) return fromCaptured;
      }
      if (row.dataset.jewelryMaterial) {
        const fromData = normalizeJewelryMaterial(row.dataset.jewelryMaterial);
        if (fromData) return fromData;
      }
    }

    let title = source.productTitle || source.product_title || source.title || '';
    if (!title && row instanceof HTMLElement) {
      const card = row.closest('[data-product-group]');
      const titleEl = card ? card.querySelector('.b2b-cart-review__product-title') : null;
      if (titleEl) title = titleEl.textContent || '';
    }
    if (title) {
      const fromTitle = normalizeJewelryMaterial(String(title).trim());
      if (fromTitle) return fromTitle;
    }

    return null;
  }

  /** @param {number} spot24KPerTroyOz */
  function calculatePricesByKarat(spot24KPerTroyOz) {
    const spot = round2(spot24KPerTroyOz);
    return {
      spot,
      '10k': round2(spot * PURITY_MULTIPLIERS['10k']),
      '14k': round2(spot * PURITY_MULTIPLIERS['14k']),
      '18k': round2(spot * PURITY_MULTIPLIERS['18k']),
      '22k': round2(spot * PURITY_MULTIPLIERS['22k']),
      '24k': round2(spot * PURITY_MULTIPLIERS['24k']),
    };
  }

  const api = {
    TROY_OUNCE_IN_GRAMS,
    PURITY_MULTIPLIERS,
    PROP,
    UPDATE_EVENT,
    normalizeJewelryMaterial,
    normalizeGoldKarat,
    toFiniteNumber,
    round2,
    materialToKaratKey,
    parseFinitePositiveNumber,
    parseWeightInGrams,
    extractPerTroyOzPrice,
    resolveGoldPricePerTroyOz,
    resolveLiveGoldPrice,
    getLiveGoldPriceMap,
    getRatesPerGram,
    getGoldPriceForMaterial,
    parseCapturedGoldPrice,
    buildCaptureProperties,
    attachCaptureProperties,
    calculateGoldPricePerGram,
    calculateFinalGoldPrice,
    extractGoldPremium,
    applyVariantTariffToGoldPerGram,
    calculateRowGoldValue,
    calculateRowTotalCents,
    calculateCartRowPricing,
    calculateRowPricing,
    calculatePricesByKarat,
    formatMoney,
    formatPerTroyOz,
    formatPerGram,
    formatCapturedTimestamp,
    formatLiveTickerTimestamp,
    getActiveCurrencyCode,
    updateFromApiPayload,
    publishSpotPrice,
    waitForReady,
    refresh,
    initFromDom,
    resolveJewelryMaterial,
    resolveCartRowMaterial,
    isMaterialOptionName,
    getStoreSnapshot: function () {
      return {
        spot24KPerTroyOz: store.spot24KPerTroyOz,
        liveGoldPricePerTroyOz: store.liveGoldPricePerTroyOz,
        goldPremium: store.goldPremium,
        finalGoldPricePerTroyOz: store.spot24KPerTroyOz,
        currency: store.currency,
        status: store.status,
        lastUpdated: store.lastUpdated,
        liveGoldPriceMap: getLiveGoldPriceMap(),
        ratesPerGram: getRatesPerGram(),
      };
    },
  };

  runtimeWindow[GLOBAL_KEY] = api;
  runtimeWindow.aoneGoldPricesReady = waitForReady();

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        initFromDom(document);
      },
      { once: true }
    );
  } else {
    initFromDom(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initFromDom(event.target);
  });
})();
